import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import { RealtimeProvider } from "@/lib/realtime";
import { ShopProvider } from "@/context/ShopContext";
import Layout from "@/components/Layout";
import AuthCallback from "@/components/AuthCallback";
import GoogleCallback from "@/components/GoogleCallback";
import Home from "@/pages/Home";
import Servers from "@/pages/Servers";
import ServerDetail from "@/pages/ServerDetail";
import TeamDetails from "@/pages/TeamDetails";
import Leaderboards from "@/pages/Leaderboards";
import Events from "@/pages/Events";
import HowToVerify from "@/pages/HowToVerify";
import Builds from "@/pages/Builds";
import BuildUpload from "@/pages/BuildUpload";
import Store from "@/pages/Store";
import About from "@/pages/About";
import Rules from "@/pages/Rules";
import Contact from "@/pages/Contact";
import Admin from "@/pages/Admin";
import Profile from "@/pages/Profile";
import UserProfile from "@/pages/UserProfile";
import Verify from "@/pages/Verify";
import EventDetail from "@/pages/EventDetail";
import CreatorHub from "@/pages/CreatorHub";
import CreatorApply from "@/pages/CreatorApply";
import CreatorProfile from "@/pages/CreatorProfile";
import SearchResults from "@/pages/SearchResults";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Wallet from "@/pages/Wallet";
import MyOrders from "@/pages/MyOrders";
import Community from "@/pages/Community";
import Communities from "@/pages/Communities";
import CommunityDetail from "@/pages/CommunityDetail";
import CreatorCommunitiesPage from "@/pages/CreatorCommunitiesPage";
import CreatorCommunityPage from "@/pages/CreatorCommunityPage";
import PlusePage from "@/pages/PlusePage";
import Messages from "@/pages/Messages";
import FollowRequests from "@/pages/FollowRequests";

const PageWrap = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -8 }}
    transition={{ duration: 0.35, ease: [0.2, 0.9, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

function AnimatedRoutes() {
  const location = useLocation();

  // Emergent Google Auth callback — handle session_id in URL fragment
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if ((location.hash || window.location.hash || "").includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route element={<Layout />}>
          <Route path="/" element={<PageWrap><Home /></PageWrap>} />
          <Route path="/servers" element={<PageWrap><Servers /></PageWrap>} />
          <Route path="/servers/:key" element={<PageWrap><ServerDetail /></PageWrap>} />
          <Route path="/teams/:serverId/:teamId" element={<PageWrap><TeamDetails /></PageWrap>} />
          <Route path="/leaderboards" element={<PageWrap><Leaderboards /></PageWrap>} />
          <Route path="/events" element={<PageWrap><Events /></PageWrap>} />
          <Route path="/how-to-verify" element={<PageWrap><HowToVerify /></PageWrap>} />
          <Route path="/events/:slug" element={<PageWrap><EventDetail /></PageWrap>} />
          <Route path="/builds" element={<PageWrap><Builds /></PageWrap>} />
          <Route path="/builds/upload" element={<PageWrap><BuildUpload /></PageWrap>} />
          <Route path="/Communities" element={<PageWrap><CreatorCommunitiesPage /></PageWrap>} />
          <Route path="/Communities/:cid" element={<PageWrap><CreatorCommunityPage /></PageWrap>} />
          <Route path="/community" element={<PageWrap><Community /></PageWrap>} />
          <Route path="/pluse" element={<PageWrap><PlusePage /></PageWrap>} />
          <Route path="/pluse/:id" element={<PageWrap><PlusePage /></PageWrap>} />
          <Route path="/hashtag/:tag" element={<PageWrap><PlusePage /></PageWrap>} />
          <Route path="/messages" element={<PageWrap><Messages /></PageWrap>} />
          <Route path="/messages/:convId" element={<PageWrap><Messages /></PageWrap>} />
          <Route path="/follow-requests" element={<PageWrap><FollowRequests /></PageWrap>} />
          <Route path="/community/:userId" element={<PageWrap><UserProfile /></PageWrap>} />
          <Route path="/community/@:handle" element={<PageWrap><UserProfile /></PageWrap>} />
          <Route path="/store" element={<PageWrap><Store /></PageWrap>} />
          <Route path="/store/:slug" element={<PageWrap><ProductDetail /></PageWrap>} />
          <Route path="/cart" element={<PageWrap><Cart /></PageWrap>} />
          <Route path="/wallet" element={<PageWrap><Wallet /></PageWrap>} />
          <Route path="/orders" element={<PageWrap><MyOrders /></PageWrap>} />
          <Route path="/about" element={<PageWrap><About /></PageWrap>} />
          <Route path="/rules" element={<PageWrap><Rules /></PageWrap>} />
          <Route path="/contact" element={<PageWrap><Contact /></PageWrap>} />
          <Route path="/admin" element={<PageWrap><Admin /></PageWrap>} />
          <Route path="/profile" element={<PageWrap><Profile /></PageWrap>} />
          <Route path="/users/:id" element={<PageWrap><UserProfile /></PageWrap>} />
          <Route path="/verify" element={<PageWrap><Verify /></PageWrap>} />
          <Route path="/get-started" element={<PageWrap><Verify /></PageWrap>} />
          <Route path="/creators" element={<PageWrap><CreatorHub /></PageWrap>} />
          <Route path="/creators/apply" element={<PageWrap><CreatorApply /></PageWrap>} />
          <Route path="/creators/:id" element={<PageWrap><CreatorProfile /></PageWrap>} />
          <Route path="/search" element={<PageWrap><SearchResults /></PageWrap>} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/google" element={<GoogleCallback />} />
        </Route>
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  return (
    <AuthProvider>
      <RealtimeProvider>
        <BrowserRouter>
          <ShopProvider>
            <AnimatedRoutes />
            <Toaster theme="dark" position="bottom-right" />
          </ShopProvider>
        </BrowserRouter>
      </RealtimeProvider>
    </AuthProvider>
  );
}

export default App;
